import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useProjects } from '../hooks/use-projects';
import { useAuth } from '../lib/auth-context';

export function ProjectsPage(): JSX.Element {
  const projects = useProjects();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isMaster = user?.role === 'MASTER';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('projects.title')}</h1>
        {isMaster && (
          <Button asChild>
            <Link to="/projects/new">
              <Plus className="h-4 w-4" /> {t('projects.newProject')}
            </Link>
          </Button>
        )}
      </div>
      {projects.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : projects.data?.items.length ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {projects.data.items.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="block">
              <Card className="transition hover:shadow-md">
                <div className="h-1.5 rounded-t-lg" style={{ backgroundColor: p.color }} />
                <CardContent className="space-y-1 p-4">
                  <p className="font-semibold">{p.name}</p>
                  <p className="line-clamp-2 text-sm text-surface-500">{p.description ?? '—'}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-sm text-surface-500">
            {isMaster ? t('projects.emptyMaster') : t('projects.emptyMember')}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
